/**
 * Singleton QueryClient for the app.
 *
 * Defaults are tuned for an app where Supabase realtime is the source of
 * freshness — we don't want react-query to refetch on every focus or
 * remount because realtime subscriptions (see useRealtimeQuery) already
 * push INSERT/UPDATE/DELETE into the cache as they happen.
 *
 * Defaults rationale:
 *
 *   staleTime: 30s
 *     Suppresses the "remount → refetch" thrash when navigating between
 *     tabs of the same screen (Tasks → Vendors → Tasks). Realtime fills
 *     the gap if a row actually changes.
 *
 *   gcTime: 5min
 *     Keeps the cache around long enough that quick back-button navs
 *     hydrate instantly. Bumps to 10min if memory becomes a concern.
 *
 *   refetchOnWindowFocus: false
 *     Realtime is the freshness mechanism. Window-focus refetch fights
 *     it (the cache is usually already correct) and burns Supabase
 *     row reads on every alt-tab.
 *
 *   refetchOnReconnect: 'always'
 *     The one case where a refetch *is* worthwhile — realtime channels
 *     drop on offline, and we don't want stale data after a long
 *     disconnect. Refetching everything that's mounted catches up.
 *
 *   retry: 1
 *     Supabase RLS denials and 4xx errors don't get better on retry,
 *     and a long retry tail makes the UI feel broken. One retry covers
 *     transient network blips; two+ would just delay the error.
 *
 *   throwOnError: false (default)
 *     Errors flow through `useQuery({ ...res, error })` so screens can
 *     render their own empty/error states without a global boundary.
 */
import { QueryClient } from '@tanstack/react-query'

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      refetchOnReconnect: 'always',
      retry: 1,
    },
    mutations: {
      retry: 0,
    },
  },
})
